/**
 * KAN-562 - Require a learner to finish their profile before the course opens.
 *
 * Harold Buck: "when I invite a user to a course, they create a login and it
 * takes them right to the course. Is it possible to make them fill out the
 * profile information before the course opens for them? Otherwise most of
 * them aren't going to do it."
 *
 * A guard on `courses/:id` is the one place that covers every route into the
 * player: the invite-signup redirect, a dashboard click, a shared deep link,
 * in-app navigation and a hard reload.
 *
 * This is a UX nudge, NOT a security boundary - there is deliberately no
 * server-side counterpart. Every uncertainty resolves in the learner's favour
 * (see profile-completion.utils for the three properties this relies on).
 *
 * Turned-away learners land on `/profile?returnTo=<course url>
 * &reason=complete-profile`, where the profile page explains why and hands
 * them back into the course once the requested fields are filled.
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { ProfileService } from '../../profile/profile.service';
import { ProfileFieldsService } from '../../profile/profile-fields/profile-fields.service';
import { isProfileGatedRole, shouldGateCoursePlayer } from './profile-completion.utils';

export const profileCompletionGuard: CanActivateFn = (_route, state) => {
  const profileService = inject(ProfileService);
  const profileFieldsService = inject(ProfileFieldsService);
  const router = inject(Router);

  const user = profileService.getCurrentUser();

  // Instructors, admins and anyone whose role we cannot read go straight in,
  // without spending a request on the profile-fields endpoint.
  if (!isProfileGatedRole(user)) {
    return true;
  }

  return profileFieldsService.getMyProfileFields().pipe(
    map(({ completion }) =>
      shouldGateCoursePlayer(user, completion)
        ? router.createUrlTree(['/profile'], {
            queryParams: { returnTo: state.url, reason: 'complete-profile' },
          })
        : true
    ),
    // Fail OPEN: a network failure or a 4xx/5xx must never lock a learner out
    // of their course. Precedent: the KAN-468 prompt hides itself on error.
    catchError(() => of(true))
  );
};
